'use client';

import { useState } from 'react';

import { useBiLoggerContext } from '@/components/Contexts/BiLoggerContext';
import { BI_CLICK } from '@/constants/bi';

import { SidebarDeviceFilter } from './SidebarDeviceFilter';
import { SidebarHeader } from './SidebarHeader';
import { SidebarPinList } from './SidebarPinList';
import { useSidebarPins } from './useSidebarPins';

import type { SidebarMode, SortOrder } from './types';
import type { CommentUserInfo, DevicePreset, Pin } from '@/types/feedback';

interface SidebarProps {
  pins: Pin[];
  device?: DevicePreset;
  allDevices?: boolean;
  mode: SidebarMode;
  commenterInfoMap?: Record<string, CommentUserInfo>;
  selectedPinId?: string | null;
  onPinClick: (id: string) => void;
  onReopenPin?: (id: string) => void;
  onClose: () => void;
}

export function Sidebar({
  pins,
  device = 'desktop',
  allDevices = true,
  mode,
  commenterInfoMap = {},
  selectedPinId,
  onPinClick,
  onReopenPin,
  onClose,
}: SidebarProps) {
  const { reportClick } = useBiLoggerContext();
  const [sortOrder, setSortOrder] = useState<SortOrder>('newest');
  const {
    deviceFilter,
    setDeviceFilter,
    filterCounts,
    visiblePinCount,
    openCommentCount,
    pinGroups,
  } = useSidebarPins({ pins, device, allDevices, sortOrder });

  const handlePinClick = (id: string) => {
    reportClick({
      clickType: BI_CLICK.SIDEBAR_PIN_CLICK,
      commentId: pins.find((p) => p.id === id)?.commentId,
    });
    onPinClick(id);
  };

  const handleToggleSort = () => {
    const next = sortOrder === 'newest' ? 'oldest' : 'newest';
    reportClick({ clickType: BI_CLICK.SIDEBAR_SORT, text: next });
    setSortOrder(next);
  };

  const handleClose = () => {
    reportClick({ clickType: BI_CLICK.SIDEBAR_CLOSE });
    onClose();
  };

  return (
    <aside
      data-hook="comments-sidebar"
      className="flex h-full w-72 flex-shrink-0 flex-col overflow-hidden bg-background"
      style={{
        borderRadius: 12,
        boxShadow:
          '0 4px 6px -1px rgba(0,0,0,0.08), 0 12px 32px -4px rgba(0,0,0,0.12)',
      }}
    >
      <SidebarHeader
        openCommentCount={openCommentCount}
        sortOrder={sortOrder}
        showSortToggle={visiblePinCount > 1}
        onToggleSort={handleToggleSort}
        onClose={handleClose}
      />
      <SidebarDeviceFilter
        value={deviceFilter}
        counts={filterCounts}
        onChange={setDeviceFilter}
      />
      <SidebarPinList
        groups={pinGroups}
        mode={mode}
        deviceFilter={deviceFilter}
        commenterInfoMap={commenterInfoMap}
        selectedPinId={selectedPinId}
        onPinClick={handlePinClick}
        onReopenPin={onReopenPin}
      />
    </aside>
  );
}
